import { useRef, useState } from 'react'
import { Loader2, Upload, X, FileDown, ScanLine, CheckCircle2, AlertCircle } from 'lucide-react'
import { uploadFile, getFileUrl } from '../../../api/qb'
import { splitOfflineSheet, openExamPaperPdf, type OfflineSplitResultItem } from '../../../api/exam'

interface UploadedSheet {
  name: string
  /** 服务器可访问的图片 URL（已带前缀），用于预览与识别 */
  url: string
}

interface OfflineAnswerPanelProps {
  sessionId: string
  paperId: string
  /** 识别结果确认填入后回调：Session 据此更新 answerMap */
  onAnswersApplied: (results: OfflineSplitResultItem[], sheetImages: string[]) => void
}

const MAX_SHEETS = 3

export function OfflineAnswerPanel({ sessionId, paperId, onAnswersApplied }: OfflineAnswerPanelProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [sheets, setSheets] = useState<UploadedSheet[]>([])
  const [uploading, setUploading] = useState(false)
  const [recognizing, setRecognizing] = useState(false)
  const [downloadingPdf, setDownloadingPdf] = useState(false)
  const [results, setResults] = useState<OfflineSplitResultItem[] | null>(null)
  const [editedMap, setEditedMap] = useState<Record<string, string>>({})
  const [error, setError] = useState<string | null>(null)
  const [applied, setApplied] = useState(false)

  async function handleFilesSelected(fileList: FileList | null) {
    if (!fileList || fileList.length === 0) return
    const files = Array.from(fileList).filter(f => f.type.startsWith('image/'))
    if (files.length === 0) return
    if (sheets.length + files.length > MAX_SHEETS) {
      setError(`最多上传 ${MAX_SHEETS} 张作答纸照片`)
      return
    }
    setError(null)
    setUploading(true)
    try {
      for (const file of files) {
        const uploaded = await uploadFile(file)
        setSheets(prev => [...prev, { name: file.name, url: getFileUrl(uploaded.url) }])
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setUploading(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  async function handleRecognize() {
    if (sheets.length === 0) return
    setError(null)
    setRecognizing(true)
    try {
      const resp = await splitOfflineSheet(sessionId, sheets.map(s => s.url))
      setResults(resp.results)
      setEditedMap({})
      setApplied(false)
      if (resp.recognizedCount === 0) {
        setError('未能识别出任何作答内容，请确认照片清晰、完整覆盖作答区域后重试')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setRecognizing(false)
    }
  }

  async function handleDownloadPdf(withAnswers: boolean) {
    setError(null)
    setDownloadingPdf(true)
    try {
      await openExamPaperPdf(paperId, withAnswers)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setDownloadingPdf(false)
    }
  }

  function handleApply() {
    if (!results) return
    const finalResults = results.map(r => ({
      ...r,
      extractedAnswer: editedMap[r.questionId] ?? r.extractedAnswer,
      matched: (editedMap[r.questionId] ?? r.extractedAnswer).length > 0,
    }))
    onAnswersApplied(finalResults, sheets.map(s => s.url))
    setApplied(true)
  }

  const recognizedCount = results?.filter(r => (editedMap[r.questionId] ?? r.extractedAnswer).length > 0).length ?? 0

  return (
    <div className="rounded-xl border border-indigo-200 bg-indigo-50/50 p-4 space-y-4">
      <div className="flex items-center gap-2">
        <ScanLine className="w-4 h-4 text-indigo-600" />
        <h2 className="text-sm font-semibold text-indigo-900">线下作答</h2>
      </div>
      <p className="text-xs text-slate-500">
        第一步：下载试卷并打印，在纸上完成作答；第二步：上传整张作答纸照片（1-{MAX_SHEETS} 张），AI 将按题号识别每道题的作答内容。
      </p>

      {/* 下载试卷 */}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => handleDownloadPdf(false)}
          disabled={downloadingPdf}
          className="inline-flex items-center px-3 py-1.5 rounded-md bg-white border border-indigo-200 text-indigo-700 text-xs hover:bg-indigo-50 disabled:opacity-60"
        >
          <FileDown className="w-3.5 h-3.5 mr-1" />
          {downloadingPdf ? '正在生成...' : '下载试卷 PDF（无答案）'}
        </button>
        <button
          type="button"
          onClick={() => handleDownloadPdf(true)}
          disabled={downloadingPdf}
          className="inline-flex items-center px-3 py-1.5 rounded-md bg-white border border-slate-200 text-slate-500 text-xs hover:bg-slate-50 disabled:opacity-60"
        >
          {downloadingPdf ? '正在生成...' : '含答案解析版'}
        </button>
      </div>

      {/* 上传作答纸照片 */}
      <div
        className="flex flex-col items-center justify-center border-2 border-dashed border-indigo-300 rounded-lg p-6 bg-white hover:bg-indigo-50/50 transition-colors cursor-pointer"
        onClick={() => !uploading && !recognizing && inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); e.stopPropagation() }}
        onDrop={(e) => {
          e.preventDefault()
          e.stopPropagation()
          handleFilesSelected(e.dataTransfer.files)
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          className="hidden"
          onChange={(e) => handleFilesSelected(e.target.files)}
        />
        {uploading ? (
          <div className="flex items-center gap-2 text-indigo-600 text-sm">
            <Loader2 className="w-5 h-5 animate-spin" />
            正在上传照片...
          </div>
        ) : (
          <>
            <Upload className="h-6 w-6 text-indigo-400 mb-1" />
            <p className="text-sm text-slate-600">点击或拖拽上传作答纸照片</p>
            <p className="text-xs text-slate-400 mt-0.5">支持 JPG、PNG、WebP，最多 {MAX_SHEETS} 张</p>
          </>
        )}
      </div>

      {/* 已上传照片预览 */}
      {sheets.length > 0 && (
        <div className="flex flex-wrap gap-3">
          {sheets.map((sheet, idx) => (
            <div key={`${sheet.name}-${idx}`} className="relative">
              <img
                src={sheet.url}
                alt={`作答纸 ${idx + 1}`}
                className="h-20 w-20 object-cover rounded-md border border-slate-200"
              />
              <button
                type="button"
                onClick={() => setSheets(prev => prev.filter((_, i) => i !== idx))}
                disabled={recognizing}
                className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* 开始识别 */}
      {(!results || !applied) && (
        <button
          type="button"
          onClick={handleRecognize}
          disabled={sheets.length === 0 || recognizing || uploading}
          className="w-full inline-flex items-center justify-center px-4 py-2 rounded-md bg-indigo-600 text-white text-sm hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {recognizing ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              AI 正在识别作答纸，通常需要 30-60 秒...
            </>
          ) : (
            '开始识别作答内容'
          )}
        </button>
      )}

      {/* 识别结果确认 */}
      {results && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-xs font-medium text-slate-600">
              识别结果：{results.length} 题中识别出 {recognizedCount} 题，可逐题修正后确认填入
            </div>
          </div>
          <div className="max-h-72 overflow-y-auto space-y-2 pr-1">
            {results.map((r) => {
              const value = editedMap[r.questionId] ?? r.extractedAnswer
              return (
                <div
                  key={r.questionId}
                  className={`rounded-md border p-2 ${r.matched || value ? 'border-slate-200 bg-white' : 'border-rose-200 bg-rose-50'}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-semibold text-slate-700">第 {r.indexInPaper} 题</span>
                    {r.matched || value ? (
                      <CheckCircle2 className="w-3 h-3 text-green-500" />
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[11px] text-rose-600">
                        <AlertCircle className="w-3 h-3" />
                        未识别，可手动填写或稍后逐题上传照片
                      </span>
                    )}
                  </div>
                  <textarea
                    className="w-full min-h-[40px] rounded border border-slate-200 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-400"
                    placeholder="该题作答内容..."
                    value={value}
                    onChange={(e) => setEditedMap(prev => ({ ...prev, [r.questionId]: e.target.value }))}
                  />
                </div>
              )
            })}
          </div>
          {applied ? (
            <div className="rounded-md bg-green-50 border border-green-200 px-3 py-2 text-xs text-green-700">
              已填入 {recognizedCount} 题作答，请核对后点击页面右上角「提交试卷」进行统一批改。
            </div>
          ) : (
            <button
              type="button"
              onClick={handleApply}
              className="w-full px-4 py-2 rounded-md bg-green-600 text-white text-sm hover:bg-green-700"
            >
              确认填入（{recognizedCount} 题）
            </button>
          )}
        </div>
      )}

      {error && <div className="text-xs text-red-500">{error}</div>}
    </div>
  )
}
